import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MaskedReveal from "@/components/MaskedReveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1567808291548-fc3ee04dbcf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjYXIlMjBkYXJrJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg0MTg1NTM0fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden pt-32 pb-16 px-6 md:px-10 bg-carbon"
      data-testid="hero-section"
    >
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <div className="w-full h-full" style={{
          backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }} />
      </div>

      <div className="relative max-w-[1600px] mx-auto grid grid-cols-12 gap-4 md:gap-6 items-end min-h-[80vh]">
        {/* Left: massive text */}
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 z-10">
          <MaskedReveal delay={0.3}>
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt">
              ◉ Mobile ECU Remapping · Nottingham × Derby × East Midlands
            </span>
          </MaskedReveal>

          <h1 className="font-display uppercase leading-[0.85] tracking-tighter mt-8 text-[13vw] lg:text-[8.5vw] xl:text-[8vw]" data-testid="hero-h1">
            <MaskedReveal delay={0.5}>
              <span className="block">Mobile car</span>
            </MaskedReveal>
            <MaskedReveal delay={0.65}>
              <span className="block">
                <span className="text-volt">remapping</span>
              </span>
            </MaskedReveal>
            <MaskedReveal delay={0.8}>
              <span className="block text-[7vw] lg:text-[4.2vw] xl:text-[4vw] leading-[1] mt-2">Nottingham &amp; Derby.</span>
            </MaskedReveal>
          </h1>

          <div className="mt-10 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-6">
              <MaskedReveal delay={1.05}>
                <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-md">
                  Mobile <strong className="text-white">ECU remapping near you</strong> — Stage 1, Stage 2 & Stage 3
                  tunes, diesel remaps, DPF solutions and gearbox tuning across
                  Nottingham, Derby and the East Midlands. We come to you.
                </p>
              </MaskedReveal>
            </div>

            <div className="col-span-12 md:col-span-6 flex md:justify-end items-end gap-3">
              <MaskedReveal delay={1.2}>
                <a
                  href="#gains"
                  className="inline-flex items-center gap-3 bg-volt text-black px-6 py-4 font-mono text-xs uppercase tracking-widest font-semibold hover:bg-white transition-colors duration-300"
                  data-testid="hero-cta-book"
                >
                  Get my gains estimate →
                </a>
              </MaskedReveal>
              <MaskedReveal delay={1.3}>
                <a
                  href="#services"
                  className="inline-flex items-center gap-3 border border-white/20 px-6 py-4 font-mono text-xs uppercase tracking-widest hover:border-white/60 transition-colors duration-300"
                  data-testid="hero-cta-services"
                >
                  Services
                </a>
              </MaskedReveal>
            </div>
          </div>
        </motion.div>

        {/* Right: clipped image */}
        <div className="col-span-12 lg:col-span-5 relative h-[60vh] lg:h-[85vh]">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.6, duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-0 overflow-hidden clipped-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Mobile car remapping specialist tuning a performance car in Nottingham and Derby"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </motion.div>

          {/* Corner readout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.6 }}
            className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-widest text-white/80 bg-black/60 backdrop-blur px-3 py-2 border border-white/10"
          >
            <div className="text-volt">◉ mobile · on-site</div>
            <div>Notts · Derby · 52.9°N</div>
          </motion.div>
        </div>
      </div>

      {/* Bottom ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative max-w-[1600px] mx-auto mt-16 flex flex-wrap items-center gap-x-10 gap-y-4 font-mono text-[11px] uppercase tracking-widest text-white/50 border-t border-white/10 pt-6"
      >
        <span><span className="text-volt">+38%</span> avg. torque uplift</span>
        <span><span className="text-volt">1200+</span> vehicles remapped</span>
        <span><span className="text-volt">100%</span> mobile · we come to you</span>
        <span className="ml-auto"><span className="text-volt">Fully insured</span> · 30-day warranty · 5.0 on Google</span>
      </motion.div>
    </section>
  );
}
